/**
 * Type-safe wrapper around localStorage with JSON encoding + a small
 * pub/sub for cross-component sync. Used to persist UI preferences
 * (card order, chart toggles, date-range selection) across sessions
 * without ever putting market data into local storage.
 *
 * All keys are namespaced under `montel-dashboard:` so they don't
 * collide with anything else a colleague may have stored on the
 * dashboard hostname.
 */

const PREFIX = "montel-dashboard:";

function fullKey(key: string): string {
  return PREFIX + key;
}

export function loadJSON<T>(key: string, fallback: T): T {
  if (typeof localStorage === "undefined") return fallback;
  try {
    const raw = localStorage.getItem(fullKey(key));
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function saveJSON<T>(key: string, value: T): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(fullKey(key), JSON.stringify(value));
  } catch {
    // Out of quota or private-mode storage failure — silently ignore.
    // The UI keeps working in-memory; only persistence is lost.
  }
}

/** Storage keys, centralised so refactors don't break persisted state. */
export const STORAGE_KEYS = {
  marketPricesOrder: "market-prices-order",
  /**
   * Per-curve chart state: visible series + selected date range + zoom.
   * Bumped to -v2 when series ids switched from rolling front codes (M1/Q1/Y1)
   * to real contract names (Jul-2026 / Q3-2026 / Cal-2027) — old persisted
   * visibility no longer matches and would otherwise hide everything.
   */
  futuresChartState: "futures-chart-state-v2",
} as const;

// ---- Per-curve chart state -----------------------------------------------

export type DateRangeId = "all" | "5y" | "3y" | "1y" | "6m" | "3m" | "1m" | "1w";

/**
 * Tenor bucket in persisted chart state. Kept structurally identical to
 * `ContractTenor` in web/src/lib/api.ts; storing it as a plain string
 * literal here avoids the storage module having to import the API types.
 */
export type ChartTenor = "month" | "quarter" | "year";

export interface FuturesChartState {
  /** Real contract ids (e.g. "Cal-2027", "Q3-2026") the user has explicitly
   *  turned on. In sticky mode the tenor's contracts are visible regardless
   *  of what's in this list — see `stickyTenors`. */
  visible: string[];
  /**
   * Tenor groups whose contracts are all visible by default, including
   * NEW contracts that appear after a month-end / quarter-end / year-end
   * rollover. Group-toggling a tenor "on" in the legend adds the tenor
   * here; toggling it "off" removes it. Clicking a single contract inside
   * a sticky group promotes the group back to explicit (per-id) mode so
   * the deselection sticks.
   *
   * Old persisted state (v2 without this field) is migrated at load time:
   * any tenor whose contracts appear in `visible` becomes sticky, so a
   * user who had "all months on" before still sees all months (plus the
   * new post-rollover ones) after the upgrade.
   */
  stickyTenors?: ChartTenor[];
  /** Active range button. */
  range: DateRangeId;
  /**
   * Optional zoom window, as a fraction of the resolved range (0..1).
   * `null` means "use the full range button window". When the user drags
   * the range slider this gets set; clicking a range button clears it.
   */
  zoom: { start: number; end: number } | null;
}

export type FuturesChartStateById = Record<string, FuturesChartState | undefined>;

export function loadFuturesChartState(curveId: string, defaults: FuturesChartState): FuturesChartState {
  const all = loadJSON<FuturesChartStateById>(STORAGE_KEYS.futuresChartState, {});
  const stored = all[curveId];
  if (!stored) return defaults;
  // Defensive merge — never trust the shape of persisted data.
  return {
    visible: Array.isArray(stored.visible) ? stored.visible : defaults.visible,
    stickyTenors: isChartTenorArray(stored.stickyTenors) ? stored.stickyTenors : undefined,
    range: isDateRangeId(stored.range) ? stored.range : defaults.range,
    zoom:
      stored.zoom &&
      typeof stored.zoom.start === "number" &&
      typeof stored.zoom.end === "number" &&
      stored.zoom.start >= 0 &&
      stored.zoom.end <= 1 &&
      stored.zoom.start < stored.zoom.end
        ? stored.zoom
        : null,
  };
}

export function saveFuturesChartState(curveId: string, state: FuturesChartState): void {
  const all = loadJSON<FuturesChartStateById>(STORAGE_KEYS.futuresChartState, {});
  all[curveId] = state;
  saveJSON(STORAGE_KEYS.futuresChartState, all);
}

function isChartTenor(v: unknown): v is ChartTenor {
  return v === "month" || v === "quarter" || v === "year";
}

function isChartTenorArray(v: unknown): v is ChartTenor[] {
  return Array.isArray(v) && v.every(isChartTenor);
}

function isDateRangeId(v: unknown): v is DateRangeId {
  return (
    v === "all" ||
    v === "5y" ||
    v === "3y" ||
    v === "1y" ||
    v === "6m" ||
    v === "3m" ||
    v === "1m" ||
    v === "1w"
  );
}
